import { fetchRemittanceExport } from '@/api';
import { DownloadIcon, PriceWithUnit, StatusTag } from '@/components';
import {
  FINANCE_AMOUNT,
  IS_CORRECTION,
  ORDER_ACTIONS,
  ORDER_STATUS,
  REMITTANCE_ID,
  STATUS,
  STYLE_PRICE,
  THEME_VARIABLE,
  TITLE,
  TRANSACTION_ID,
  ROUTE,
} from '@/config';
import {
  FinanceCOD,
  FinanceTab,
  FinanceTransaction,
  PermissionName,
  RemittanceStatus,
} from '@/types';
import { getRemittanceStatusTagData, triggerDownload } from '@/utils';
import { Flex, notification, Spin } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { generatePath } from 'react-router-dom';
import { ProcessNameEnum } from '@/stores/pollingMechanismStore';
import { LoadingOutlined } from '@ant-design/icons';
import { ArrowUpDown, CircleArrowDown, CircleArrowUp, ClipboardList } from 'lucide-react';

enum TransactionStatusColorEnum {
  ENABLED = 'enabled',
  PENDING = 'pending',
  PAID = 'paid',
}

// Pill badge for remittance/transaction status matching exact redesign spec
function RemittancePillBadge({ status, label }: { status: RemittanceStatus | string; label: string }) {
  const styleMap: Record<string, { color: string; background: string }> = {
    delivered: {
      color: 'rgb(39,155,101)',
      background: 'rgba(49,196,127,0.12)',
    },
    pending: {
      color: 'rgb(187,120,27)',
      background: 'rgba(245,158,11,0.12)',
    },
    requested: {
      color: 'rgb(59,130,246)',
      background: 'rgba(59,130,246,0.12)',
    },
    cancelled: {
      color: 'rgb(220,40,40)',
      background: 'rgba(220,40,40,0.12)',
    },
    initiated: {
      color: 'rgb(124,58,237)',
      background: 'rgba(124,58,237,0.12)',
    },
    ready_to_pickup: {
      color: 'rgb(187,120,27)',
      background: 'rgba(245,158,11,0.12)',
    },
  };

  const slug = (typeof status === 'string' ? status : String(status)).toLowerCase();
  const styles = styleMap[slug] ?? {
    color: 'rgb(115,123,140)',
    background: 'rgba(115,123,140,0.12)',
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '9999px',
        padding: '4px 10px',
        color: styles.color,
        background: styles.background,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// Pill badge for transaction status using same colour logic
function TransactionPillBadge({ status, label }: { status: string; label: string }) {
  const styleMap: Record<string, { color: string; background: string }> = {
    enabled: {
      color: 'rgb(59,130,246)',
      background: 'rgba(59,130,246,0.12)',
    },
    pending: {
      color: 'rgb(187,120,27)',
      background: 'rgba(245,158,11,0.12)',
    },
    paid: {
      color: 'rgb(39,155,101)',
      background: 'rgba(49,196,127,0.12)',
    },
  };

  const slug = (status ?? '').toLowerCase();
  const styles = styleMap[slug] ?? {
    color: 'rgb(115,123,140)',
    background: 'rgba(115,123,140,0.12)',
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '9999px',
        padding: '4px 10px',
        color: styles.color,
        background: styles.background,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export const getColumns = (
  activeTab: FinanceTab,
  t: (key: string) => string,
  downloadingUid: string | null,
  setDownloadingUid: (uid: string | null) => void,
  openEntityTicketModal: (platformCode?: string, uid?: string) => void,
  hasPermission: (...permissions: string[]) => boolean,
  startPolling?: (
    processName: ProcessNameEnum,
    toastData: { fileName: string; toastTextKey?: string },
    checkStatusFunction: () => Promise<boolean>,
    timeout?: number,
    customIntervalTime?: number,
  ) => void,
) => {
  const downloadStatementAsBlob = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      triggerDownload(blobUrl, fileName);
    } catch {
      // Fallback to direct link
      triggerDownload(url, fileName);
    }
  };

  const checkRemittanceExport = (uid: string) => async () => {
    try {
      const exportData = await fetchRemittanceExport(uid);
      if (exportData.object.status === 'SUCCESS') {
        notification.success({
          message: t('download_ready'),
          description: t('your_file_is_downloading'),
          showProgress: true,
          duration: 5,
        });
        const file = exportData.object.file;
        await downloadStatementAsBlob(file.abs_url, file.filename || `statement-${uid}.pdf`);
        setTimeout(() => setDownloadingUid(null), 2000);
        return true;
      }
      if (exportData.object.status === 'FAILED') {
        notification.error({
          message: t('operation_failed_please_try_again'),
          duration: 5,
        });
        setDownloadingUid(null);
        return true;
      }
      return false;
    } catch {
      setDownloadingUid(null);
      return true;
    }
  };

  function getTransactionStatusTagData(status?: TransactionStatusColorEnum) {
    if (!status) return undefined;
    const statusTagData = {
      [TransactionStatusColorEnum.ENABLED]: {
        iconColor: THEME_VARIABLE?.customBlue,
        tagColor: 'processing',
      },
      [TransactionStatusColorEnum.PENDING]: {
        iconColor: THEME_VARIABLE?.customOrange,
        tagColor: 'warning',
      },
      [TransactionStatusColorEnum.PAID]: {
        iconColor: THEME_VARIABLE?.customGreen,
        tagColor: 'success',
      },
    };

    return statusTagData[status];
  }

  /**
   * COD Remittances columns — redesign layout (5 columns, no actions):
   * Remittance (ID + date stacked) | Status (pill) | Method | Orders | Amount
   */
  const financeCODColumns: ColumnsType<FinanceCOD> = [
    {
      title: t('remittance'),
      dataIndex: REMITTANCE_ID,
      key: REMITTANCE_ID,
      sorter: true,
      render: (text, record) => (
        <div className='flex flex-col gap-0.5'>
          <span className='text-[13px] font-bold text-[rgb(57,57,60)]'>{text}</span>
          <span className='text-[11px] text-[rgb(115,123,140)]'>{record.date}</span>
        </div>
      ),
    },
    {
      title: t('status'),
      dataIndex: ORDER_STATUS,
      key: ORDER_STATUS,
      render: (text: RemittanceStatus) => (
        <RemittancePillBadge status={text} label={t(text)} />
      ),
    },
    {
      title: t('method'),
      key: 'payment_method',
      render: (_text, record) => (
        <span className='text-[13px] text-[rgb(57,57,60)]'>
          {record.payment_method || 'COD'}
        </span>
      ),
    },
    {
      title: t('orders'),
      key: 'orders_count',
      align: 'center' as const,
      render: (_text, record) => (
        <span className='text-[13px] text-[rgb(57,57,60)]'>
          {record.orders_count != null ? record.orders_count : '—'}
        </span>
      ),
    },
    {
      title: t('amount'),
      dataIndex: FINANCE_AMOUNT,
      key: FINANCE_AMOUNT,
      align: 'right' as const,
      render: (text) => (
        <span className='text-[13px] font-bold text-[rgb(57,57,60)] whitespace-nowrap'>
          {text ? `${Number(text).toLocaleString()} IQD` : '—'}
        </span>
      ),
    },
    {
      title: t('actions'),
      key: ORDER_ACTIONS,
      width: '10%',
      fixed: 'right' as const,
      render: (_: unknown, record: FinanceCOD) => (
        <div className='flex items-center gap-1.5'>
          <button
            type='button'
            className='flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors border-0'
            onClick={(e) => {
              e.stopPropagation();
              openEntityTicketModal(record.remittanceId, record.key as string);
            }}
            disabled={!hasPermission(PermissionName.CREATE_TICKET)}
          >
            <ClipboardList size={13} />
            {t('ticket')}
          </button>
        </div>
      ),
    },
  ];

  /**
   * Transactions columns — redesign layout (3 columns):
   * Transaction (icon + ID + date) | Amount (colored) | Status (pill + correction inline)
   */
  const financeTransactionColumns: ColumnsType<FinanceTransaction> = [
    {
      title: t('transaction'),
      dataIndex: TRANSACTION_ID,
      key: TRANSACTION_ID,
      render: (text, record) => {
        const amount = Number(record.amount);
        const isCredit = amount >= 0;
        return (
          <div className='flex items-center gap-2.5'>
            <div
              className='shrink-0 w-8 h-8 rounded-full flex items-center justify-center'
              style={{
                background: isCredit
                  ? 'rgba(34,197,94,0.12)'
                  : 'rgba(239,68,68,0.12)',
              }}
            >
              {isCredit ? (
                <CircleArrowDown
                  className='w-4 h-4'
                  style={{ color: 'rgb(34,197,94)' }}
                />
              ) : (
                <CircleArrowUp
                  className='w-4 h-4'
                  style={{ color: 'rgb(239,68,68)' }}
                />
              )}
            </div>
            <div className='flex flex-col gap-0.5'>
              <span className='text-[13px] font-bold text-[rgb(57,57,60)]'>{text}</span>
              {record.date && (
                <span className='text-[11px] text-[rgb(115,123,140)]'>{record.date}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t('order_id'),
      key: 'order_platform_code',
      render: (_: unknown, record: FinanceTransaction) => (
        <span className='text-[13px] font-medium text-[rgb(57,57,60)]'>
          {record.order_platform_code || '—'}
        </span>
      ),
    },
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => {
        // Translate transaction type slugs like "order_fee" → "Order Fee"
        const key = `transaction_type_${text}`;
        const translated = String(t(key)) !== key ? String(t(key)) : (text || '—').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        return (
          <span className='text-[13px] text-[rgb(57,57,60)] max-w-[150px] truncate block'>{translated}</span>
        );
      },
    },
    {
      title: t('description'),
      dataIndex: 'details',
      key: 'details',
      render: (text: string) => {
        // Humanize raw claim type slugs in descriptions like "(partially_damaged)" → "(Partially Damaged)"
        const formatted = text && text !== '---'
          ? text.replace(/\(([a-z_]+)\)/g, (_match, slug: string) => {
              const key = `claim_type_${slug}`;
              const translated = String(t(key)) !== key ? String(t(key)) : slug.replace(/_/g, ' ');
              return `(${translated.charAt(0).toUpperCase() + translated.slice(1)})`;
            })
          : '—';
        return (
          <span className='text-[12px] text-[rgb(115,123,140)] max-w-[300px] block' style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>{formatted}</span>
        );
      },
    },
    {
      title: t('amount'),
      dataIndex: FINANCE_AMOUNT,
      key: FINANCE_AMOUNT,
      render: (text, record) => {
        const numericAmount = Number(record.amount);
        const isPositive = numericAmount >= 0;
        const color = isPositive ? 'rgb(34,197,94)' : 'rgb(239,68,68)';
        const prefix = isPositive ? '+' : '';
        const formatted = text ? `${prefix}${Number(text).toLocaleString()} IQD` : '—';
        return (
          <span
            className='text-[13px] font-bold whitespace-nowrap'
            style={{ color }}
          >
            {formatted}
          </span>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: STATUS,
      key: STATUS,
      render: (text, record) => (
        <div className='flex items-center gap-1.5 flex-wrap'>
          <TransactionPillBadge
            status={text ?? ''}
            label={t(`transaction_status.${text}`)}
          />
          {record.is_correction === 1 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '11px',
                fontWeight: 600,
                borderRadius: '9999px',
                padding: '4px 10px',
                color: 'rgb(161,115,27)',
                background: 'rgba(234,179,8,0.12)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('correction')}
            </span>
          )}
        </div>
      ),
    },
  ];

  switch (activeTab) {
    case FinanceTab.COD:
      return financeCODColumns;

    case FinanceTab.Transactions:
      return financeTransactionColumns;

    default:
      return [];
  }
};
